import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, RefreshCw, Loader2, X, Send, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";
import { useDashboardWidgetPanel } from "../../lib/useDashboardWidgetPanel";

/**
 * Compact "Jobs Today · X need attention" tile for the admin dashboard
 * header. Hits GET /api/jobs/collection-email-audit on mount + every
 * 60 s, and flashes red when any completed booking today has an
 * unresolved silent email failure.
 *
 * Clicking the tile opens an inline expandable panel listing every
 * red-flagged booking with a one-click "Resend now" button per row —
 * so a silent problem is impossible to miss AND can be fixed in three
 * seconds without leaving the current tab.
 */
const CollectionEmailAuditWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { open, toggle, close, panelRef, tileRef } = useDashboardWidgetPanel("collection-audit");
  const [resendingId, setResendingId] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jobs/collection-email-audit`);
      setData(res.data);
      setLastFetched(new Date());
    } catch (err) {
      // Endpoint requires admin; staff will silently get 403.
      // Non-admin case: just hide the widget entirely (see render).
      if (err.response?.status === 403 || err.response?.status === 401) {
        setData({ __hidden: true });
      } else {
        // Log but don't spam toasts — this poll happens every 60s.
        console.warn("Audit poll failed:", err.response?.data || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const handleResend = async (bookingId) => {
    setResendingId(bookingId);
    try {
      const res = await axios.post(`${API}/jobs/resend-collection-email/${bookingId}`);
      toast.success(
        `Email resent to ${res.data.customer_email}${res.data.resend_id ? ` (${res.data.resend_id.slice(0, 8)}…)` : ""}`,
      );
      // Optimistic: refresh right after so the tile updates from red → green
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Resend failed: ${detail}`);
    } finally {
      setResendingId(null);
    }
  };

  const needsAttentionRows = useMemo(
    () => (data?.rows || []).filter((r) => r.needs_attention),
    [data],
  );

  if (!data || data.__hidden) return null;

  const need = data.needs_attention_count || 0;
  const total = data.completed_count || 0;
  const isRed = need > 0;

  return (
    <div className="relative" data-testid="collection-audit-widget">
      <button
        ref={tileRef}
        type="button"
        onClick={toggle}
        className={[
          "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-all",
          isRed
            ? "bg-rose-500/15 border-rose-500/40 text-rose-300 hover:bg-rose-500/25 animate-pulse"
            : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20",
        ].join(" ")}
        title={isRed
          ? `${need} booking(s) today with an unresolved email failure — click to fix`
          : `All ${total} completed booking(s) today have their emails delivered`}
        data-testid="collection-audit-toggle"
        data-audit-status={isRed ? "red" : "green"}
      >
        {isRed
          ? <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
        <span className="whitespace-nowrap">
          Jobs today · <strong className="tabular-nums">{total}</strong>
          {isRed && (
            <>
              {" "}·{" "}
              <span className="text-rose-100 font-bold">
                {need} need attention
              </span>
            </>
          )}
        </span>
        {loading && <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />}
      </button>

      {open && (
        <>
          {/* Backdrop — heavier dim + blur so it's visible on the dark
              theme (bg-black/40 was imperceptible against the near-black
              admin UI). Full-viewport `fixed` so it covers metric cards
              regardless of DOM position. */}
          <div className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none" aria-hidden />
          {/* Panel — on mobile: fixed to viewport centre-top so it
              renders identically no matter which tile opened it (fixes
              the left-edge cutoff when the leftmost tile is the source).
              On desktop (sm+): revert to the tile-anchored dropdown so
              the panel appears beneath the button that opened it. */}
          <div
            ref={panelRef}
            className="fixed left-2 right-2 top-24 max-w-md mx-auto z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:mx-0 w-auto sm:w-[380px] bg-slate-900 border border-slate-700 rounded-lg shadow-2xl"
            data-testid="collection-audit-panel"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
              <div className="text-white text-sm font-semibold">
                Email audit · {data.date}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={load}
                  disabled={loading}
                  className="text-slate-400 hover:text-white p-1 rounded"
                  title="Refresh now"
                  data-testid="collection-audit-refresh"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
                <button
                  onClick={close}
                  className="text-slate-400 hover:text-white p-1 rounded"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {needsAttentionRows.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-slate-300 text-sm">
                  All {total} completed booking{total === 1 ? "" : "s"} today have their emails delivered.
                </p>
                {lastFetched && (
                  <p className="text-slate-500 text-xs mt-2">
                    Last check {lastFetched.toLocaleTimeString()}
                  </p>
                )}
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-800">
                {needsAttentionRows.map((row) => (
                  <div
                    key={row.booking_id}
                    className="p-3 hover:bg-slate-800/40"
                    data-testid={`audit-row-${row.booking_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-medium truncate">
                          {row.customer_name || "Unnamed customer"}
                        </div>
                        <div className="text-slate-400 text-xs truncate">
                          {row.customer_email || "(no email on file)"}
                        </div>
                        {row.vehicle && (
                          <div className="text-slate-500 text-xs truncate">
                            {row.vehicle}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleResend(row.booking_id)}
                        disabled={resendingId === row.booking_id || !row.customer_email}
                        className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs bg-saturn-orange hover:bg-orange-400 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded"
                        data-testid={`audit-resend-${row.booking_id}`}
                        title={row.customer_email ? "Resend collection email" : "No email address on file"}
                      >
                        {resendingId === row.booking_id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Send className="w-3 h-3" />}
                        Resend
                      </button>
                    </div>
                    {row.all_errors.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {row.all_errors.map((err, i) => (
                          <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-rose-500/15 text-rose-300 border border-rose-500/30 font-mono"
                            title={err}
                          >
                            {err.length > 42 ? err.slice(0, 40) + "…" : err}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {data.error_frequency && Object.keys(data.error_frequency).length > 0 && (
              <div className="border-t border-slate-800 px-3 py-2 bg-slate-950/50">
                <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">
                  Today&apos;s error patterns
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(data.error_frequency).map(([reason, count]) => (
                    <span
                      key={reason}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-mono"
                      title="Same reason across N bookings usually means a systemic misconfig — check /jobs/environment-check"
                    >
                      {reason} × {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CollectionEmailAuditWidget;
